/**
 * Every change to a project is an Op. Ops are grouped in Transactions.
 * Applying a transaction returns the new project and the inverse transaction,
 * which gives undo/redo for free. The AI assistant and (later) CRDT sync use the
 * same mechanism: they produce transactions, never mutate state directly.
 */
import type {
  Circuit,
  CustomSymbol,
  Element,
  Id,
  Inspection,
  Invoice,
  Measurement,
  Project,
  ProjectMeta,
  Remark,
  Revision,
  Sheet,
  SurveyBoard,
  SurveyCircuit,
  SurveyPoint,
  SurveyRoom,
  Wire,
} from './types.ts';

export const COLLECTIONS = [
  'sheets',
  'elements',
  'wires',
  'circuits',
  'revisions',
  'symbols',
  'surveyRooms',
  'surveyPoints',
  'surveyBoards',
  'surveyCircuits',
  'measurements',
  'inspections',
  'remarks',
  'invoices',
] as const;
export type Collection = (typeof COLLECTIONS)[number];
type EntityOf<C extends Collection> = Project[C][string];

export type Op =
  | { op: 'add'; coll: 'sheets'; value: Sheet }
  | { op: 'add'; coll: 'elements'; value: Element }
  | { op: 'add'; coll: 'wires'; value: Wire }
  | { op: 'add'; coll: 'circuits'; value: Circuit }
  | { op: 'add'; coll: 'revisions'; value: Revision }
  | { op: 'add'; coll: 'symbols'; value: CustomSymbol }
  | { op: 'add'; coll: 'surveyRooms'; value: SurveyRoom }
  | { op: 'add'; coll: 'surveyPoints'; value: SurveyPoint }
  | { op: 'add'; coll: 'surveyBoards'; value: SurveyBoard }
  | { op: 'add'; coll: 'surveyCircuits'; value: SurveyCircuit }
  | { op: 'add'; coll: 'measurements'; value: Measurement }
  | { op: 'add'; coll: 'inspections'; value: Inspection }
  | { op: 'add'; coll: 'remarks'; value: Remark }
  | { op: 'add'; coll: 'invoices'; value: Invoice }
  | { op: 'remove'; coll: Collection; id: Id }
  | { op: 'update'; coll: 'sheets'; id: Id; patch: Partial<Omit<Sheet, 'id'>> }
  | { op: 'update'; coll: 'elements'; id: Id; patch: Partial<Omit<Element, 'id'>> }
  | { op: 'update'; coll: 'wires'; id: Id; patch: Partial<Omit<Wire, 'id'>> }
  | { op: 'update'; coll: 'circuits'; id: Id; patch: Partial<Omit<Circuit, 'id'>> }
  | { op: 'update'; coll: 'revisions'; id: Id; patch: Partial<Omit<Revision, 'id'>> }
  | { op: 'update'; coll: 'symbols'; id: Id; patch: Partial<Omit<CustomSymbol, 'id'>> }
  | { op: 'update'; coll: 'surveyRooms'; id: Id; patch: Partial<Omit<SurveyRoom, 'id'>> }
  | { op: 'update'; coll: 'surveyPoints'; id: Id; patch: Partial<Omit<SurveyPoint, 'id'>> }
  | { op: 'update'; coll: 'surveyBoards'; id: Id; patch: Partial<Omit<SurveyBoard, 'id'>> }
  | { op: 'update'; coll: 'surveyCircuits'; id: Id; patch: Partial<Omit<SurveyCircuit, 'id'>> }
  | { op: 'update'; coll: 'measurements'; id: Id; patch: Partial<Omit<Measurement, 'id'>> }
  | { op: 'update'; coll: 'inspections'; id: Id; patch: Partial<Omit<Inspection, 'id'>> }
  | { op: 'update'; coll: 'remarks'; id: Id; patch: Partial<Omit<Remark, 'id'>> }
  | { op: 'meta'; patch: Partial<ProjectMeta> };

export interface Transaction {
  label: string;
  ops: Op[];
}

export class CommandError extends Error {}

/** Previous values of `keys`; null for a field that was not set (survives JSON, unlike undefined). */
function pick<T extends object>(obj: T, keys: string[]): Partial<T> {
  const out: Record<string, unknown> = {};
  for (const k of keys) out[k] = (obj as Record<string, unknown>)[k] ?? null;
  return out as Partial<T>;
}

/**
 * Merge a patch. A null or undefined value removes the field (optional fields are never null
 * in a project), so clearing a field also works after the op went through JSON (sync).
 */
function merge<T extends object>(base: T, patch: object): T {
  const out = { ...base } as Record<string, unknown>;
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined || v === null) delete out[k];
    else out[k] = v;
  }
  return out as T;
}

/**
 * Mutable working copy used inside one transaction: each touched collection is
 * shallow-copied once, so a transaction of n ops costs O(n), not O(n * size).
 */
class Draft {
  readonly p: Project;
  private copied = new Set<string>();
  constructor(base: Project) {
    this.p = { ...base };
  }
  coll(name: Collection): Record<Id, EntityOf<Collection>> {
    if (!this.copied.has(name)) {
      (this.p as unknown as Record<string, unknown>)[name] = { ...(this.p[name] ?? {}) };
      this.copied.add(name);
    }
    return this.p[name] as Record<Id, EntityOf<Collection>>;
  }
}

/** Apply one op to the draft; returns the op that undoes it. */
function applyOp(d: Draft, op: Op): Op {
  switch (op.op) {
    case 'add': {
      const coll = d.coll(op.coll);
      if (coll[op.value.id]) throw new CommandError(`${op.coll}/${op.value.id} already exists`);
      coll[op.value.id] = op.value;
      return { op: 'remove', coll: op.coll, id: op.value.id };
    }
    case 'remove': {
      const coll = d.coll(op.coll);
      const existing = coll[op.id];
      if (!existing) throw new CommandError(`${op.coll}/${op.id} not found`);
      delete coll[op.id];
      return { op: 'add', coll: op.coll, value: existing } as Op;
    }
    case 'update': {
      const coll = d.coll(op.coll);
      const existing = coll[op.id];
      if (!existing) throw new CommandError(`${op.coll}/${op.id} not found`);
      const before = pick(existing, Object.keys(op.patch));
      coll[op.id] = merge(existing, op.patch);
      return { op: 'update', coll: op.coll, id: op.id, patch: before } as Op;
    }
    case 'meta': {
      const before = pick(d.p.meta, Object.keys(op.patch));
      (d.p as { meta: ProjectMeta }).meta = merge(d.p.meta, op.patch);
      return { op: 'meta', patch: before };
    }
  }
}

/** Apply a transaction atomically. Throws (leaving the input untouched) if any op fails. */
export function applyTransaction(
  project: Project,
  tx: Transaction,
): { project: Project; inverse: Transaction } {
  const d = new Draft(project);
  const inverseOps: Op[] = [];
  for (const op of tx.ops) inverseOps.push(applyOp(d, op));
  inverseOps.reverse();
  return { project: d.p, inverse: { label: tx.label, ops: inverseOps } };
}

/** Ops that remove an element together with every wire attached to it. */
export function removeElementOps(project: Project, elementId: Id): Op[] {
  const ops: Op[] = [];
  for (const w of Object.values(project.wires)) {
    const attached =
      (w.from.kind === 'port' && w.from.elementId === elementId) ||
      (w.to.kind === 'port' && w.to.elementId === elementId);
    if (attached) ops.push({ op: 'remove', coll: 'wires', id: w.id });
  }
  ops.push({ op: 'remove', coll: 'elements', id: elementId });
  return ops;
}

/** Ops that remove a sheet and everything on it. */
export function removeSheetOps(project: Project, sheetId: Id): Op[] {
  const ops: Op[] = [];
  for (const w of Object.values(project.wires))
    if (w.sheetId === sheetId) ops.push({ op: 'remove', coll: 'wires', id: w.id });
  for (const e of Object.values(project.elements))
    if (e.sheetId === sheetId) ops.push({ op: 'remove', coll: 'elements', id: e.id });
  ops.push({ op: 'remove', coll: 'sheets', id: sheetId });
  return ops;
}
